import { cn } from '@startupquickstart/frontend';
import { Camera, Pause, Play } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isVideoProofUrl, type ProofMediaType } from '@/lib/media';
import { DECK } from './displayTheme';

interface SlideshowPhoto {
  id: string;
  photoUrl: string;
  /** Additive API field; older payloads omit it (extension fallback applies). */
  mediaType?: ProofMediaType;
  submittedAt: string;
  submittedBy: string | null;
  teamId: string;
  teamName: string;
  challengeId: string;
  challengeName: string;
}

interface SlideshowEvent {
  id: string;
  name: string;
  logoUrl: string | null;
  backgroundColor: string | null;
  accentColor: string | null;
  slideshowIntervalSeconds: number;
}

interface DisplaySlideshowProps {
  event: SlideshowEvent;
  photos: SlideshowPhoto[];
}

/**
 * DT-166. Post-event photo slideshow in the deck language: each photo
 * sits in a rounded frame on the branded stage with a slow Ken Burns
 * drift, captioned with a mono team eyebrow and serif challenge title.
 * An accent progress bar drains per photo; branding (background,
 * accent, logo) comes from the event row, with the deck navy / pin
 * green as the default look.
 *
 * Click anywhere or press Space to pause / resume; ArrowRight skips.
 */
export function DisplaySlideshow({ event, photos }: DisplaySlideshowProps) {
  const intervalMs = Math.max(2_000, event.slideshowIntervalSeconds * 1000);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // Two-layer cross-fade: the back layer holds the previous photo at
  // the moment of advance so the new front layer fades in over it.
  const [previousIndex, setPreviousIndex] = useState<number | null>(null);
  const [isFading, setIsFading] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const advance = useCallback(() => {
    if (photos.length === 0) return;
    setPreviousIndex(index);
    setIndex((current) => (current + 1) % photos.length);
    setIsFading(true);
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => {
      setIsFading(false);
      setPreviousIndex(null);
    }, 800);
  }, [photos.length, index]);

  useEffect(() => {
    if (paused || photos.length <= 1) return;
    const t = setInterval(advance, intervalMs);
    return () => clearInterval(t);
  }, [paused, photos.length, intervalMs, advance]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        e.preventDefault();
        setPaused((p) => !p);
      } else if (e.key === 'ArrowRight') {
        advance();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [advance]);

  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, []);

  const accent = event.accentColor || DECK.pin;
  const stageStyle = useMemo(
    () => ({ backgroundColor: event.backgroundColor || DECK.navy }),
    [event.backgroundColor],
  );

  if (photos.length === 0) {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center gap-4"
        style={{ ...stageStyle, color: 'rgba(248,247,244,0.6)' }}
      >
        <Camera className="h-14 w-14 opacity-40" />
        <p className="text-3xl font-semibold" style={{ color: DECK.paper }}>
          No photos yet
        </p>
        <p className="font-mono text-xs uppercase tracking-[0.2em]">
          Photos appear here as teams submit them
        </p>
      </div>
    );
  }

  const current = photos[index];
  const previous = previousIndex !== null ? photos[previousIndex] : null;

  return (
    <div
      className="relative flex h-full w-full flex-col overflow-hidden"
      style={stageStyle}
      onClick={() => setPaused((p) => !p)}
    >
      <style>{`
        @keyframes dspSlideFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dspKenBurns {
          from { transform: scale(1); }
          to { transform: scale(1.06); }
        }
        @keyframes dspDrain { from { width: 0%; } to { width: 100%; } }
        @media (prefers-reduced-motion: reduce) {
          .dsp-kenburns, .dsp-drain { animation: none !important; }
        }
      `}</style>

      {/* Framed photo */}
      <div className="relative min-h-0 flex-1 px-16 pb-4 pt-8">
        <div
          className="relative mx-auto h-full max-w-6xl overflow-hidden rounded-3xl ring-1 ring-black/20"
          style={{ boxShadow: '0 24px 80px rgba(2,6,23,0.55)', backgroundColor: '#0b1120' }}
        >
          {previous &&
            (isVideoProofUrl(previous.photoUrl, previous.mediaType) ? (
              <video
                key={`prev-${previous.id}`}
                src={previous.photoUrl}
                autoPlay
                muted
                loop
                playsInline
                className={cn(
                  'absolute inset-0 h-full w-full object-contain transition-opacity duration-700',
                  isFading ? 'opacity-0' : 'opacity-100',
                )}
              />
            ) : (
              <img
                key={`prev-${previous.id}`}
                src={previous.photoUrl}
                alt=""
                className={cn(
                  'absolute inset-0 h-full w-full object-contain transition-opacity duration-700',
                  isFading ? 'opacity-0' : 'opacity-100',
                )}
              />
            ))}
          {isVideoProofUrl(current.photoUrl, current.mediaType) ? (
            // Videos loop for the slide interval; no Ken Burns drift —
            // the footage supplies its own motion.
            <video
              key={`curr-${current.id}`}
              src={current.photoUrl}
              autoPlay
              muted
              loop
              playsInline
              aria-label={`${current.teamName} — ${current.challengeName}`}
              className="absolute inset-0 h-full w-full object-contain"
              style={{ animation: 'dspSlideFadeIn 0.7s ease-out' }}
            />
          ) : (
            <img
              key={`curr-${current.id}`}
              src={current.photoUrl}
              alt={`${current.teamName} — ${current.challengeName}`}
              className="dsp-kenburns absolute inset-0 h-full w-full object-contain"
              style={{
                animation: `dspSlideFadeIn 0.7s ease-out, dspKenBurns ${Math.max(intervalMs, 6000)}ms ease-out forwards`,
              }}
            />
          )}

          {/* Per-photo progress bar along the frame's bottom edge */}
          {!paused && photos.length > 1 && (
            <div className="absolute inset-x-0 bottom-0 h-1 bg-black/30">
              <div
                key={`drain-${current.id}`}
                className="dsp-drain h-full"
                style={{
                  backgroundColor: accent,
                  animation: `dspDrain ${intervalMs}ms linear forwards`,
                }}
              />
            </div>
          )}

          {/* Counter — top-right of the frame */}
          <div className="pointer-events-none absolute right-5 top-5 rounded-full bg-black/50 px-3.5 py-1 font-mono text-xs tabular-nums text-white/90">
            {index + 1} / {photos.length}
          </div>

          {/* Pause chip — centered over the frame */}
          {paused && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="flex items-center gap-2.5 rounded-full bg-black/60 px-5 py-2.5 text-white">
                <Pause className="h-4 w-4" />
                <span className="font-mono text-xs uppercase tracking-[0.15em]">
                  Paused — click or Space to resume
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Caption band */}
      <div className="flex flex-shrink-0 items-end justify-between px-16 pb-7 pt-1">
        <div className="min-w-0">
          <p
            className="truncate font-mono text-[0.6875rem] font-medium uppercase tracking-[0.2em]"
            style={{ color: accent }}
          >
            {current.teamName}
            {current.submittedBy ? ` · by ${current.submittedBy}` : ''}
          </p>
          <p
            className="truncate text-3xl font-semibold tracking-tight"
            style={{ color: DECK.paper }}
          >
            {current.challengeName}
          </p>
        </div>

        <div className="flex flex-shrink-0 items-center gap-4 pl-8">
          <span
            className="flex items-center gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.15em]"
            style={{ color: 'rgba(248,247,244,0.4)' }}
          >
            {paused ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            {paused ? 'paused' : `${event.slideshowIntervalSeconds}s per photo`}
          </span>
          {event.logoUrl && (
            <img
              src={event.logoUrl}
              alt={event.name}
              className="max-h-14 w-auto object-contain opacity-90"
            />
          )}
        </div>
      </div>
    </div>
  );
}
